<template>
  <div class="modern-master-view pb-12">
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm">
          <v-icon color="white">fas fa-balance-scale</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Conciliación bancaria</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
            Extracto vs movimientos de tesorería
          </div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn outlined color="primary" class="font-weight-black text-uppercase tracking-widest rounded-lg px-6" :loading="loading" @click="fetchAll">
        <v-icon left small>fas fa-redo</v-icon> Actualizar
      </v-btn>
    </v-row>

    <v-row class="mb-6 px-4">
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Líneas sin conciliar</div>
          <div class="text-h5 font-weight-black error--text">{{ summary.unmatched_statement_lines || 0 }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Líneas conciliadas</div>
          <div class="text-h5 font-weight-black success--text">{{ summary.matched_statement_lines || 0 }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Movimientos libres</div>
          <div class="text-h5 font-weight-black">{{ summary.unmatched_movements || 0 }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Extractos abiertos</div>
          <div class="text-h5 font-weight-black">{{ summary.open_imports || 0 }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="modern-card pa-6 mb-6 mx-4">
      <v-row dense align="center">
        <v-col cols="12" md="4">
          <v-select v-model="accountId" :items="accountItems" item-text="text" item-value="value" outlined dense hide-details clearable label="Cuenta" class="modern-input" @change="fetchAll" />
        </v-col>
        <v-col cols="12" md="8" class="d-flex justify-end" style="gap: 8px;">
          <v-btn outlined color="primary" :disabled="!accountId" :loading="matching" @click="runAutoMatch">Auto-conciliar</v-btn>
          <v-btn color="primary" class="font-weight-bold" :disabled="!accountId" @click="importDialog = true">Importar CSV</v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-card outlined class="modern-card mx-4 mb-6">
      <div class="pa-4 subtitle-2 font-weight-bold">Extractos</div>
      <v-data-table :headers="importHeaders" :items="imports" hide-default-footer class="elevation-0" no-data-text="Importe un CSV de extracto (columnas fecha y monto).">
        <template v-slot:item.status="{ item }">
          <v-chip x-small>{{ item.status_label }}</v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn x-small text color="primary" @click="selectImport(item)">Ver líneas</v-btn>
          <v-btn v-if="item.status === 'open'" x-small text @click="closeImport(item)">Cerrar</v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-card outlined class="modern-card mx-4">
      <div class="pa-4 subtitle-2 font-weight-bold">Líneas {{ selectedImport ? ('· extracto #' + selectedImport.id) : '' }}</div>
      <v-data-table :headers="lineHeaders" :items="lines" :loading="loadingLines" hide-default-footer class="elevation-0" no-data-text="Seleccione un extracto.">
        <template v-slot:item.signed_amount="{ item }">
          <span :class="item.direction === 'in' ? 'success--text font-weight-bold' : 'error--text font-weight-bold'">{{ item.signed_amount | moneda }}</span>
        </template>
        <template v-slot:item.match_status="{ item }">
          <v-chip x-small :color="item.match_status === 'matched' ? 'success' : (item.match_status === 'ignored' ? 'grey' : 'warning')" class="white--text">
            {{ item.match_status_label }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on"><v-icon small>fas fa-ellipsis-v</v-icon></v-btn>
            </template>
            <v-list dense>
              <v-list-item v-if="item.match_status === 'unmatched'" @click="createFromLine(item)">
                <v-list-item-title>Crear movimiento y conciliar</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="item.match_status === 'unmatched'" @click="ignoreLine(item)">
                <v-list-item-title>Ignorar</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="item.match_status === 'matched'" @click="unmatchLine(item)">
                <v-list-item-title>Desconciliar</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="importDialog" max-width="640">
      <v-card>
        <v-card-title class="font-weight-black">Importar extracto CSV</v-card-title>
        <v-card-text>
          <div class="caption mb-3">Encabezados: fecha, monto (positivo = ingreso). Opcional: descripcion, referencia. Separador coma o punto y coma.</div>
          <v-textarea v-model="csvText" outlined rows="10" label="Pegue el CSV" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="importDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="importing" @click="submitImport">Importar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import {
  autoMatchRecon,
  closeReconImport,
  createMovementFromReconLine,
  createReconImport,
  fetchReconImports,
  fetchReconLines,
  fetchReconSummary,
  fetchTreasuryAccounts,
  ignoreReconLine,
  unmatchReconLine,
} from '@/api/finance'

export default {
  name: 'FinanceReconciliation',
  data() {
    return {
      loading: false,
      loadingLines: false,
      matching: false,
      importing: false,
      accountId: '',
      accounts: [],
      summary: {},
      imports: [],
      lines: [],
      selectedImport: null,
      importDialog: false,
      csvText: '',
      importHeaders: [
        { text: 'Cuenta', value: 'account_code' },
        { text: 'Desde', value: 'period_from' },
        { text: 'Hasta', value: 'period_to' },
        { text: 'Líneas', value: 'line_count' },
        { text: 'Conciliadas', value: 'matched_count' },
        { text: 'Estado', value: 'status' },
        { text: '', value: 'actions', sortable: false },
      ],
      lineHeaders: [
        { text: 'Fecha', value: 'line_date' },
        { text: 'Descripción', value: 'description' },
        { text: 'Referencia', value: 'reference' },
        { text: 'Monto', value: 'signed_amount', align: 'end' },
        { text: 'Estado', value: 'match_status' },
        { text: '', value: 'actions', sortable: false, width: 56 },
      ],
    }
  },
  computed: {
    accountItems() {
      return this.accounts.map(acc => ({ value: acc.id, text: `${acc.code} · ${acc.name}` }))
    },
  },
  methods: {
    async fetchAll() {
      this.loading = true
      try {
        const [summary, accounts, imports] = await Promise.all([
          fetchReconSummary({ bank_account_id: this.accountId || undefined }),
          fetchTreasuryAccounts(),
          fetchReconImports({ bank_account_id: this.accountId || undefined }),
        ])
        this.summary = summary
        this.accounts = accounts.items || []
        this.imports = imports.items || []
        if (this.selectedImport) {
          await this.selectImport(this.selectedImport)
        }
      } catch (e) {
        notifications.showError('No se pudo cargar conciliación')
      } finally {
        this.loading = false
      }
    },
    async selectImport(item) {
      this.selectedImport = item
      this.loadingLines = true
      try {
        const data = await fetchReconLines({ import_id: item.id })
        this.lines = data.items || []
      } catch (e) {
        notifications.showError('No se pudieron cargar las líneas')
      } finally {
        this.loadingLines = false
      }
    },
    async submitImport() {
      if (!this.accountId) {
        notifications.showError('Seleccione una cuenta')
        return
      }
      this.importing = true
      try {
        const created = await createReconImport({
          bank_account_id: this.accountId,
          csv_text: this.csvText,
          filename: 'paste.csv',
        })
        notifications.showSuccess(`Importadas ${created.line_count || 0} líneas`)
        this.importDialog = false
        this.csvText = ''
        await this.fetchAll()
        if (created.id) await this.selectImport(created)
      } catch (e) {
        notifications.showError((e.response && e.response.data && e.response.data.error) || 'No se pudo importar')
      } finally {
        this.importing = false
      }
    },
    async runAutoMatch() {
      this.matching = true
      try {
        const res = await autoMatchRecon({
          bank_account_id: this.accountId || undefined,
          import_id: this.selectedImport && this.selectedImport.id,
        })
        notifications.showSuccess(`Conciliadas ${res.matched || 0} · omitidas ${res.skipped || 0}`)
        await this.fetchAll()
      } catch (e) {
        notifications.showError('No se pudo auto-conciliar')
      } finally {
        this.matching = false
      }
    },
    async closeImport(item) {
      try {
        await closeReconImport(item.id)
        notifications.showSuccess('Extracto cerrado')
        await this.fetchAll()
      } catch (e) {
        notifications.showError('No se pudo cerrar')
      }
    },
    async createFromLine(item) {
      try {
        await createMovementFromReconLine(item.id)
        notifications.showSuccess('Movimiento creado y conciliado')
        await this.fetchAll()
      } catch (e) {
        notifications.showError((e.response && e.response.data && e.response.data.error) || 'No se pudo crear')
      }
    },
    async ignoreLine(item) {
      try {
        await ignoreReconLine(item.id)
        await this.fetchAll()
      } catch (e) {
        notifications.showError('No se pudo ignorar')
      }
    },
    async unmatchLine(item) {
      try {
        await unmatchReconLine(item.id)
        await this.fetchAll()
      } catch (e) {
        notifications.showError('No se pudo desconciliar')
      }
    },
  },
  created() {
    this.fetchAll()
  },
}
</script>
